/* Pocket Factory first-order offer. It never interrupts: it opens from the offer tab (window.PocketEmailOffer.open()),
   or by itself only for a shopper who showed buying intent and backed out (Oct 21): they tapped a buy, quick-buy,
   checkout or Shop Pay / wallet button (pf_buy_intent), then came back to the store without ordering (back from
   checkout, the tab visible again) with nothing in the cart, or try to leave after tapping buy (desktop: pointer out the
   top of the window; phone: the back button). Never while the cart holds a design or after "Add another design"
   (persona round 2, Oct 26: an order under way keeps to the bar's offer strip). Scrolling, idling and time on page never
   open it. It never opens for a shopper who has ordered (pf_purchased, localStorage plus a 1-year cookie, set from
   customer.orders_count, an order page, or a cart that was checked out), who already signed up or has a code, and at
   most once per 7 days per browser. Never within 10 s of a purpose, pack or design change or of typing anywhere on the
   page, while the configurator or the website form has focus, during a call or an open chat with the assistant, on a
   page without the product section (D17), or in the first 8 s after the page (or a returning tab) is seen; a return
   opening also waits for a pause in scrolling and tapping (Oct 24). Asked for, it opens at once. The signup is sent
   without leaving the page when Shopify takes it that way, else the browser posts it as before (C-01, Oct 26). Then the
   Worker-issued code is shown, remembered in this browser until an order is placed, applied to the cart, and checkout
   goes through /discount/<code> whenever the cart does not confirm it. */
(function (root) {
  'use strict';
  if (typeof document === 'undefined') return;
  const dialog = document.getElementById('PFEmailOffer');
  if (!dialog || dialog.dataset.packTopReady) return;
  dialog.dataset.packTopReady = 'true';
  const form = dialog.querySelector('form');
  const params = new URLSearchParams(root.location.search);
  const role = String(dialog.dataset.themeRole || root.Shopify?.theme?.role || '').toLowerCase();
  const preview = params.has('preview_theme_id') || !!root.Shopify?.designMode || (!!role && role !== 'main');
  const qa = preview && params.get('pf_email_offer_test') === '1';
  const scope = preview ? '.preview.' + (dialog.dataset.themeId || root.Shopify?.theme?.id || params.get('preview_theme_id') || 'editor') : '';
  const shownKey = 'pf.email50.packtop.v3.shown' + scope;
  const claimedKey = 'pf.email50.requested' + scope;
  const pendingKey = 'pf.email50.pending' + scope;
  const codeKey = 'pf.email50.code' + scope;
  const linkedKey = 'pf.email50.codeLinked' + scope;
  const nonceKey = 'pf.email50.nonce';
  const emailKey = 'pf.email50.signupEmail';
  const placeKey = 'pf.email50.returnAt'; // where the page was when the signup was sent (the page Shopify returns to)
  const codeArmKey = 'pf.email50.codeArm' + scope;
  // First-order A/B test (Oct 26; layout/theme.liquid): the arm whose words this page shows, <html data-pf-offer>, or the
  // fallback until it is known. The signup carries it (customer tag pf-offer-<arm> and the Worker request), so the code
  // covers the packs the dialog named; the Worker answers with the arm the code was minted for.
  const ARM_PACKS = { packs_1_5: [1, 2, 3, 5], packs_5_10: [5, 10] }, FALLBACK_ARM = 'packs_5_10';
  const pageArm = () => {
    const arm = document.documentElement.dataset.pfOffer;
    return Object.hasOwn(ARM_PACKS, arm || '') ? arm : FALLBACK_ARM;
  };
  const armPending = () => document.documentElement.classList.contains('pf-offer-pending');
  const storage = (kind, key, value) => {
    try {
      if (value === undefined) return root[kind].getItem(key);
      if (value === null) root[kind].removeItem(key);
      else root[kind].setItem(key, String(value));
    } catch (_) { /* Storage denial must not prevent the offer from opening. */ }
    return null;
  };
  const excludedPage = /\/(?:cart|checkouts?|account|orders)(?:\/|$)/.test(root.location.pathname);
  const expiresAt = Date.parse(dialog.dataset.expiresAt);
  const result = form?.querySelector('[data-pf-email-result]')?.dataset.pfEmailResult;

  function capture(name, properties) {
    if (preview) return;
    try {
      root.posthog?.capture(name, Object.assign({ campaign: 'launch-email50-packtop-v3' }, properties || {}));
    } catch (_) { /* Analytics may never block signup or purchase. */ }
  }

  // Oct 21: popup_shown / popup_signup for the intent-trigger rollout. In theme previews analytics stay off (as above)
  // and the events are kept on window.__pfOfferEvents for QA instead.
  function captureOffer(name, properties) {
    if (preview) { try { (root.__pfOfferEvents = root.__pfOfferEvents || []).push([name, properties || {}]); } catch (_) {} return; }
    capture(name, properties);
  }
  const intentKey = 'pf_buy_intent', purchasedKey = 'pf_purchased', lastShownKey = 'pf.email50.lastShown' + scope;
  const weekMs = 7 * 864e5, intentMs = 3 * 864e5;
  const cookie = name => { try { return (document.cookie.match('(?:^|; )' + name + '=([^;]*)') || [])[1] || ''; } catch (_) { return ''; } };
  function purchased() {
    return storage('localStorage', purchasedKey) === '1' || cookie(purchasedKey) === '1';
  }
  // FIRST-PRICE-1: html.pf-first-done hides the first-order prices on the pack tiles (pocket-cro.css) once this browser has ordered.
  function firstDone() { try { document.documentElement.classList.add('pf-first-done'); } catch (_) {} }
  if (purchased() || (Number.isFinite(expiresAt) && Date.now() >= expiresAt)) firstDone();
  function markPurchased(source) {
    firstDone();
    if (purchased()) return;
    storage('localStorage', purchasedKey, '1');
    try { document.cookie = purchasedKey + '=1; max-age=31536000; path=/; SameSite=Lax'; } catch (_) {}
    storage('localStorage', intentKey, null);
    capture('pf_email_offer_suppressed_purchased', { source });
  }
  function intent() {
    try {
      const value = JSON.parse(storage('localStorage', intentKey) || 'null');
      if (value && Number.isFinite(value.t) && Date.now() - value.t < intentMs) return value;
    } catch (_) {}
    return null;
  }
  function recordIntent(source) {
    const before = intent();
    const value = { t: Date.now(), src: source, cart: cookie('cart') || before?.cart || '', path: root.location.pathname };
    storage('localStorage', intentKey, JSON.stringify(value));
    return value;
  }
  function shownRecently() {
    const at = Number(storage('localStorage', lastShownKey));
    return Number.isFinite(at) && at > 0 && Date.now() - at < weekMs;
  }
  // Anyone who has ordered never sees the offer again.
  if (dialog.dataset.hasOrdered === 'true') markPurchased('customer_orders');
  if (/\/(?:account\/)?orders(?:\/|$)|\/thank[_-]you(?:\/|$)/.test(root.location.pathname)) markPurchased('order_page');

  /* The revealed code is applied to the cart, shown truthfully in the cart drawer and on /cart, and carried to
     checkout through /discount/<code> unless /cart.js confirms it. */
  const codePattern = /^[A-Z0-9][A-Z0-9_-]{3,63}$/;
  const shopRoot = String(document.body?.dataset.root || root.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let cartState = null, syncing = null, syncAgain = false, applyTried = '';
  // One discount everywhere (Q02b, Oct 26 2026): a shopper holding the abandoned-checkout code (layout/theme.liquid sets
  // <html data-pf-offer-held>) gets that code's 25% and nothing here: no 50% offer opens, no 50% line is painted, and
  // checkout is never sent through a saved 50% code (Shopify keeps one code, so it would replace theirs).
  function offerHeld() { return !!document.documentElement.getAttribute('data-pf-offer-held'); }
  function savedCode() {
    if (offerHeld()) return '';
    const code = String(storage('localStorage', codeKey) || '').trim().toUpperCase();
    return codePattern.test(code) ? code : '';
  }
  function rememberCode(value, arm) {
    const code = String(value || '').trim().toUpperCase();
    if (!codePattern.test(code)) return;
    storage('localStorage', codeKey, code);
    storage('localStorage', codeArmKey, Object.hasOwn(ARM_PACKS, arm || '') ? arm : 'legacy');
    codeChanged();
  }
  // The bar's and the order box's code lines, and the checkout total pocket.js says with other designs in the cart
  // (PocketOffer.total), follow the code this browser holds.
  function codeChanged() {
    try { paintBarCode(); } catch (_) { /* The bar's line waits for the next change. */ }
    try { document.dispatchEvent(new root.CustomEvent('pocket:offer-code')); } catch (_) { /* The total waits too. */ }
  }
  // The packs the saved code covers: the arm the Worker minted it for, or "legacy" (a code from before the test, or one
  // this browser saved before the Worker said its arm), whose packs only checkout knows.
  function codeArm() {
    const arm = storage('localStorage', codeArmKey);
    return Object.hasOwn(ARM_PACKS, arm || '') ? arm : 'legacy';
  }
  function forgetCode() {
    storage('localStorage', codeKey, null);
    storage('localStorage', codeArmKey, null);
    cartState = null;
    renderCartStatus();
    codeChanged();
  }
  function codeOnCart(cart, code) {
    return (Array.isArray(cart?.discount_codes) ? cart.discount_codes : [])
      .find(entry => String(entry?.code || '').trim().toUpperCase() === code) || null;
  }
  function confirmed(code) {
    return !!code && cartState?.code === code && cartState.applied;
  }
  // A line's pack from its variant title, "5 Cards" or "5 Cards / Matte" (pocket-core.js packSize), else null.
  function packOf(item) {
    const title = String(item?.variant_title || item?.variant_options?.[0] || '');
    if (typeof root.PocketCore?.packSize === 'function') return root.PocketCore.packSize(title);
    const match = title.trim().match(/^(\d+)\s+Cards?\b/i);
    return match ? Number(match[1]) : null;
  }
  // None of the cart's packs is one the code covers: the line says what to switch to. A line whose pack can't be read never
  // counts as uncovered, and a legacy code's packs aren't known here.
  function noneCovered(cart, arm) {
    const covered = ARM_PACKS[arm];
    const packs = (Array.isArray(cart?.items) ? cart.items : []).map(packOf);
    return !!covered && packs.length > 0 && packs.every(pack => pack != null && !covered.includes(pack));
  }
  // Each arm's words (Oct 26, 2026; before: packs of up to 3 cards, Oct 23, then up to 10, Release 1).
  const ARM_WORDS = {
    packs_1_5: { covers: 'packs of 1–5 cards', either: 'packs of 1–5 cards (1, 2, 3 or 5)', regular: 'packs of 10 or more are regular price' },
    packs_5_10: { covers: 'packs of 5–10 cards', either: 'packs of 5–10 cards (5 or 10)', regular: 'other packs are regular price' },
  };
  const usd = cents => '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  // The cart's lines whose pack can be read, each with its price before any discount, in cents.
  function cartLines(cart) {
    return (Array.isArray(cart?.items) ? cart.items : []).map(item => ({
      pack: packOf(item), count: Math.max(1, Number(item?.quantity) || 1),
      cents: Number(item?.original_line_price ?? item?.line_price ?? Number(item?.price) * (Number(item?.quantity) || 1))
    })).filter(line => line.pack != null);
  }
  // What the code does to this cart (persona round 2, Oct 26: 05-rachel N6, 06-carlos C-02, 08-omar F6: the bar said
  // "$39.50 at checkout", the cart only "$79" and the packs the code covers): each covered pack at half its price, the
  // bar's rule (paintBarCode), then the packs at regular price. Display only: checkout applies the code. Null when no
  // covered line has a price to show.
  function cartSavings(lines, arm) {
    const covers = ARM_PACKS[arm];
    if (!covers) return null;
    const sizes = pick => Array.from(lines.filter(pick).reduce((by, line) => {
      const size = by.get(line.pack) || { pack: line.pack, count: 0, cents: 0 };
      size.count += line.count; size.cents += line.cents;
      return by.set(line.pack, size);
    }, new Map()).values());
    const many = n => ['', 'a', 'two', 'three', 'four', 'five'][n] || String(n);
    const list = parts => parts.length < 2 ? parts.join('') : parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
    const own = size => size.count === 1 ? `your ${size.pack}-card pack is` : `your ${many(size.count)} ${size.pack}-card packs are`;
    const covered = sizes(line => covers.includes(line.pack)), other = sizes(line => !covers.includes(line.pack));
    if (!covered.length || !covered.every(size => size.cents > 0)) return null;
    const saving = list(covered.map(size => `${own(size)} ${usd(Math.round(size.cents / 2))} instead of ${usd(size.cents)}`));
    const regular = !other.length ? '' : other.length === 1 ? ` ${own(other[0]).replace(/^y/, 'Y')} regular price.`
      : ` Your ${list(other.map(size => (size.count > 1 ? many(size.count) + ' ' : '') + size.pack + '-card'))} packs are regular price.`;
    return { saving, regular };
  }
  /**
   * What checkout charges for an order with the saved code, for the product page's checkout total (pocket.js
   * othersNote; persona round 3, Oct 26, 07-omar F3 and 02-laura F3: "Checkout total: $428." beside "With your 50% code:
   * $74.50 at checkout." for a $353.50 checkout). The Worker's rule (applyLaunchPricing): each line whose pack the code
   * covers at half its price, the others at theirs. Display only: checkout applies the code.
   * lines: [{ title: the variant's title ("10 Cards / Glossy / Black"), cents: the line's price before any discount }].
   * Returns null when no code is held (or after an order); { cents: null } when only checkout can say (the code's packs
   * are not known here, a line's pack can't be read, or a covered price doesn't halve to whole cents); else { cents, off },
   * off when the code takes something off.
   */
  function orderTotal(lines) {
    if (!savedCode() || purchased()) return null;
    const covers = ARM_PACKS[codeArm()];
    let cents = 0, off = false;
    for (const line of Array.isArray(lines) ? lines : []) {
      const pack = packOf({ variant_title: line?.title }), price = Number(line?.cents), half = !!covers && covers.includes(pack);
      if (!covers || pack == null || !Number.isInteger(price) || price < 0 || (half && price % 2)) return { cents: null, off: false };
      cents += half ? price / 2 : price;
      off = off || half;
    }
    return { cents, off };
  }
  function renderCartStatus() {
    const code = savedCode();
    const applied = confirmed(code);
    const arm = codeArm(), words = ARM_WORDS[arm];
    const big = !applied && !!code && cartState?.code === code && cartState.bigOnly === true && !!words;
    const told = words && code && cartState?.code === code ? cartSavings(cartState.lines || [], arm) : null;
    const lead = 'Your 50% code ';
    const rest = !words ? ' goes to checkout with this cart. Checkout applies it to the packs it covers.'
      : big ? ` covers ${words.either}. Switch to one of those packs to use it; ${words.regular}.`
        : told ? ` goes to checkout with this cart: ${told.saving}.${told.regular}`
          : ` goes to checkout with this cart. It covers ${words.covers}; ${words.regular}.`;
    const done = !words ? '50% first-order discount applied.' : told ? `50% first-order discount applied: ${told.saving}.${told.regular}`
      : `50% first-order discount applied to ${words.covers}. ${words.regular.charAt(0).toUpperCase()}${words.regular.slice(1)}.`;
    const text = !code ? '' : applied ? done : lead + code + rest;
    document.querySelectorAll('#PocketCart .cart-total, .cart-page .cart-total').forEach(total => {
      let status = total.nextElementSibling?.hasAttribute('data-pf-offer-cart-status') ? total.nextElementSibling : null;
      if (!text) { status?.remove(); return; }
      if (!status) {
        status = document.createElement('p');
        status.className = 'pf-offer-cart-status ph-no-capture';
        status.setAttribute('data-pf-offer-cart-status', '');
        status.setAttribute('data-ph-no-capture', '');
        status.setAttribute('role', 'status');
        total.after(status);
      }
      status.dataset.state = applied ? 'applied' : big ? 'packs' : 'pending';
      if (status.textContent === text) return;
      if (applied) { status.textContent = text; return; }
      const slot = document.createElement('span');
      slot.className = 'pf-offer-cart-status__code';
      slot.textContent = code;
      status.replaceChildren(lead, slot, rest);
    });
  }
  async function cartJson(path, init) {
    const response = await root.fetch(shopRoot + path, Object.assign({ credentials: 'same-origin' }, init, {
      headers: Object.assign({ Accept: 'application/json' }, init?.headers)
    }));
    if (!response.ok) throw new Error('cart ' + response.status);
    return response.json();
  }
  function syncCart() {
    if (syncing) { syncAgain = true; return syncing; }
    syncing = (async () => {
      const code = savedCode();
      renderCartStatus();
      if (!code || typeof root.fetch !== 'function') return;
      let cart = null;
      try {
        cart = await cartJson('cart.js');
        const attempt = code + ':' + (cart?.item_count ?? '');
        // Never put on a cart none of whose packs it covers: checkout would show it refused (LONGRUN-1102 round 2).
        if (!codeOnCart(cart, code) && applyTried !== attempt && !noneCovered(cart, codeArm())) {
          applyTried = attempt;
          await cartJson('cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: code })
          });
          cart = await cartJson('cart.js');
        }
      } catch (_) { /* Checkout still carries the code through /discount/<code>. */ }
      if (savedCode() === code) {
        cartState = { code, applied: codeOnCart(cart, code)?.applicable === true, bigOnly: noneCovered(cart, codeArm()), lines: cartLines(cart) };
        linkCode(code);
      }
      renderCartStatus();
    })().catch(() => {}).finally(() => {
      syncing = null;
      if (syncAgain) { syncAgain = false; syncCart(); }
    });
    return syncing;
  }
  function linkCode(code) {
    // Quick-pay buttons start checkout from the product form, not the cart. Shopify's discount link also sets the
    // discount cookie checkout reads, so it is requested once per session in the background.
    if (storage('sessionStorage', linkedKey) === code) return;
    storage('sessionStorage', linkedKey, code);
    root.fetch('/discount/' + encodeURIComponent(code) + '?redirect=' + shopRoot + 'cart.js', { credentials: 'same-origin' }).catch(() => {});
  }
  function checkoutUrl(url) {
    const target = String(url || shopRoot + 'checkout');
    const code = savedCode();
    if (!code || confirmed(code) || (cartState?.code === code && cartState.bigOnly)) return target;
    try {
      const parsed = new URL(target, root.location.href);
      if (parsed.origin !== root.location.origin || !/\/checkouts?(?:\/|$)/.test(parsed.pathname)) return target;
      return '/discount/' + encodeURIComponent(code) + '?redirect=' +
        encodeURIComponent(parsed.pathname + parsed.search + parsed.hash).replace(/%2F/gi, '/');
    } catch (_) { return target; }
  }
  function edited(target) {
    return Array.from(target.elements || []).some(field => {
      if (field.type === 'checkbox' || field.type === 'radio') return field.checked !== field.defaultChecked;
      if (field.tagName === 'SELECT') return Array.from(field.options).some(option => option.selected !== option.defaultSelected);
      return field.tagName !== 'BUTTON' && 'defaultValue' in field && field.value !== field.defaultValue;
    });
  }
  // Cart drawer and /cart checkout buttons post the cart form; send them through the discount link instead.
  document.addEventListener('submit', event => {
    if (event.defaultPrevented || event.submitter?.name !== 'checkout') return;
    const code = savedCode();
    const target = checkoutUrl(shopRoot + 'checkout');
    if (!code || !target.startsWith('/discount/')) return;
    try {
      if (edited(event.target)) {
        // Edited quantities on /cart must still reach Shopify, so this form posts and carries the code itself.
        let field = event.target.querySelector('input[type="hidden"][name="discount"]');
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'discount';
          event.target.appendChild(field);
        }
        field.value = code;
        return;
      }
      event.preventDefault();
      document.dispatchEvent(new root.CustomEvent('pocket:offer-checkout', { detail: { url: target } }));
      root.location.assign(target);
    } catch (_) { /* Never hold up checkout. */ }
  });
  const drawer = document.getElementById('PocketCart');
  if (drawer && typeof MutationObserver === 'function') {
    let wait = 0;
    const observer = new MutationObserver(() => {
      if (!drawer.open || !savedCode()) return;
      root.clearTimeout(wait);
      wait = root.setTimeout(syncCart, 150);
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
    const items = drawer.querySelector('[data-cart-items]');
    if (items) observer.observe(items, { childList: true });
  }
  // − or + on a line, in the drawer or on /cart (pocket.js): the line says what the code does to the cart as it is now.
  let lineWait = 0;
  document.addEventListener('pocket:cart-line', () => {
    if (!savedCode()) return;
    root.clearTimeout(lineWait);
    lineWait = root.setTimeout(syncCart, 150);
  });
  root.addEventListener('pageshow', event => { if (event.persisted && savedCode()) syncCart(); });
  root.PocketOffer = { checkoutUrl, code: () => savedCode() || null, total: orderTotal };
  if (dialog.dataset.hasOrdered === 'true') forgetCode();
  // A saved code is never re-checked in the background: the signup endpoint is only called by the shopper's own submit.
  else if (savedCode() && result !== 'success') syncCart();
  // pocket.js runs first: the checkout total it says takes the held code from now on.
  if (savedCode()) { try { document.dispatchEvent(new root.CustomEvent('pocket:offer-code')); } catch (_) { /* its next paint */ } }

  if (!form || typeof dialog.showModal !== 'function') return;
  let shown = storage('sessionStorage', shownKey) === '1';
  let claimed = storage('localStorage', claimedKey) === '1';
  let reached = false, checkout = false, frame = 0, paymentUntil = 0;
  let previousFocus = null, responsePending = false;
  let pendingWords = '', wordsTimer = 0, copyTimer = 0, readyFor = ''; // the success state's live region (say())
  // Calm entrance (Oct 24): no automatic opening in the first settleMs after the page, a page restored from the
  // back/forward cache, or this tab coming back is seen (seenAt); a return opening also waits calmMs after the last
  // scroll, tap or key. The offer tab and the page Shopify returns to after the signup are not automatic.
  const startedAt = Date.now(), quietMs = 10000, settleMs = 8000, calmMs = 1500;
  const composer = '[data-pocket-product] form, [data-rv-url-form]';
  let changedAt = 0, activeAt = startedAt, previewAt = 0;
  let seenAt = document.visibilityState === 'visible' ? startedAt : 0, onCall = false, chatOpen = false;
  const settling = () => !seenAt || Date.now() - seenAt < settleMs;

  function visible(element) {
    return !!element && !element.closest('[hidden],[inert],[aria-hidden="true"]') &&
      element.getClientRects().length > 0 && root.getComputedStyle(element).visibility !== 'hidden';
  }
  function temporarilyBlocked() {
    if (document.visibilityState !== 'visible' || checkout || Date.now() < paymentUntil) return true;
    const active = document.activeElement;
    if (active && !dialog.contains(active) && visible(active) &&
      (active.matches('input,textarea,select,[contenteditable="true"]') || active.isContentEditable)) return true;
    return Array.from(document.querySelectorAll('dialog[open],[aria-modal="true"]')).some(
      element => element !== dialog && visible(element)
    ) || [document.documentElement, document.body].some(node => node?.classList.contains('pf-keyboard-open'));
  }
  // Automatic openings wait 10 s after any configuration change and never cover a focused configurator.
  function interrupting() {
    return Date.now() - changedAt < quietMs || !!document.activeElement?.closest?.(composer);
  }
  function eligible() {
    return !offerHeld() && !excludedPage && (!Number.isFinite(expiresAt) || Date.now() < expiresAt) &&
      !shown && !claimed && dialog.dataset.subscribed !== 'true' && dialog.dataset.hasOrdered !== 'true' &&
      !purchased() && !savedCode() && !shownRecently();
  }
  // Shopify's bot protection (hCaptcha) owns a signup submit it holds: nothing here cancels it or posts the form instead
  // (a submit nobody holds is sent by fetch, C-01, below). When the browser posts, Shopify returns to
  // /?customer_posted=true#PFEmailOfferForm, which drops the address's ?card=, so the card's
  // slug is kept for that page (pocket-card-questions.liquid puts ?card= back before the card loads). It is kept when the
  // dialog opens and again on submit, in case another submit listener stops the event before this one runs.
  function rememberCard() {
    try {
      const card = new URLSearchParams(root.location.search).get('card');
      if (card && /^[a-z0-9][a-z0-9-]{2,63}$/.test(card)) storage('sessionStorage', 'pf.email50.returnCard', card);
    } catch (_) {}
  }
  // The card being made on this page, when the visitor's arm covers its pack: "Your 5 Glossy cards: $34.50 instead of
  // $69." in place of the arm's example (persona round 1, Oct 26: "Your first two cards. $14.50." read as their price
  // with 5 cards chosen). Prices are the variant's own, halved; nothing here changes what checkout charges.
  // The variant the card on this page would be bought as (its pack, finish and color), or null.
  function pageVariant() {
    try {
      const product = document.querySelector('[data-pocket-product]'), id = String(product?.querySelector('[data-variant-id]')?.value || '');
      return id ? (JSON.parse(product.querySelector('[data-variant-map]')?.textContent || '[]').find(v => String(v.id) === id) || null) : null;
    } catch (_) { return null; }
  }
  const dollars = cents => '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  function paintMine() {
    const title = dialog.querySelector('#PFEmailOfferTitle'), mine = title?.querySelector('[data-pf-offer-mine]'), minePrice = title?.querySelector('[data-pf-offer-mine-price]');
    if (!mine || !minePrice) return;
    const variant = pageVariant(), pack = variant ? packOf({ variant_title: variant.title }) : null, cents = Number(variant?.price), finish = String(variant?.option2 || '');
    const covered = !!pack && (ARM_PACKS[pageArm()] || []).includes(pack) && cents > 0;
    const money = dollars;
    mine.textContent = covered ? `Your ${pack === 1 ? 'card' : pack + (/^(Matte|Glossy)$/.test(finish) ? ' ' + finish : '') + ' cards'}:` : '';
    minePrice.textContent = covered ? `${money(Math.round(cents / 2))} instead of ${money(cents)}.` : '';
    if (covered && pack === 1 && /^(Matte|Glossy)$/.test(finish)) mine.textContent = `Your ${finish} card:`;
    mine.hidden = minePrice.hidden = !covered;
    // The fine print's example goes too while the headline names their own card (persona round 2, Oct 26).
    dialog.querySelectorAll('[data-pf-offer-example]').forEach(node => { node.hidden = covered; });
  }
  // The buy bar's line once this browser holds a code (persona round 1, Oct 26: after the signup the bar still said $59
  // and never said 3 cards were not covered): what the pack on the card costs with it at checkout, or the packs it covers.
  function paintBarCode() {
    // The bar's line and the order box's (a desktop shows the order box, not the bar).
    const lines = [...document.querySelectorAll('[data-pf-code-held]')];
    if (!lines.length) return;
    const code = savedCode(), covers = ARM_PACKS[codeArm()], words = ARM_WORDS[codeArm()];
    let text = '';
    if (code && !purchased() && words) {
      const variant = pageVariant(), pack = variant ? packOf({ variant_title: variant.title }) : null, cents = Number(variant?.price);
      // Another design in the cart (pocket.js: data-others on the bar): the note beside this line says what checkout
      // charges for all of it, with the code (PocketOffer.total), so this line names the packs only, never a second price
      // (persona round 3, Oct 26: "With your 50% code: $74.50 at checkout." read as the whole bill).
      const others = !!document.querySelector('[data-buy-bar][data-others]');
      text = !others && pack && covers.includes(pack) && cents > 0 ? `With your 50% code: ${dollars(Math.round(cents / 2))} at checkout.` : `Your 50% code covers ${words.covers}.`;
    }
    for (const line of lines) {
      if (line.textContent !== text) line.textContent = text;
      line.hidden = !text;
    }
  }
  function show(reason, force) {
    if (offerHeld()) { if (dialog.open) { try { dialog.close(); } catch (_) {} } return false; }
    // Opened at the first paint of the page Shopify returns to (the snippet's inline opener, C-01): taken over here.
    const early = dialog.open && dialog.hasAttribute('data-offer-early');
    // A browser that has ordered (pf_purchased, P0 Oct 27) never sees the offer, not even from a tap on the offer.
    if (!early && purchased()) return false;
    if ((dialog.open && !early) || (!early && temporarilyBlocked()) || (!force && !eligible())) return false;
    // The arm is known before the dialog opens (its packs are on it): at most the head script's 2 s on a first visit.
    if (armPending()) {
      document.addEventListener('pf:offer-arm', () => show(reason, force), { once: true });
      return false;
    }
    previousFocus = early ? null : document.activeElement;
    try { paintMine(); } catch (_) { /* The arm's example stays. */ }
    if (!early) {
      // The page Shopify returns to after the signup shows the same offer again: no entrance, it is simply there.
      dialog.toggleAttribute('data-offer-instant', reason === 'signup_response');
      try { dialog.showModal(); } catch (_) { return false; }
      if (!dialog.open) return false;
      dialog.scrollTop = 0;
    }
    dialog.removeAttribute('data-offer-early');
    shown = true;
    rememberCard();
    prepareSignup(false);
    storage('sessionStorage', shownKey, '1');
    storage('localStorage', lastShownKey, String(Date.now()));
    if (!force) storage('localStorage', intentKey, null); // one automatic opening per buying attempt
    capture('pf_email_offer_shown', { trigger: reason, offer_arm: pageArm() });
    captureOffer('popup_shown', { trigger: reason });
    // Focus goes to the dialog itself (tabindex="-1"): its name and description are read first, Tab reaches Close
    // next, and no focus ring lands on a button nobody pressed. The modal keeps Tab inside it. Taken over, it stays
    // where the shopper has put it.
    if (!early || !dialog.contains(document.activeElement)) (dialog.hasAttribute('tabindex') ? dialog : dialog.querySelector('[data-offer-close]'))?.focus({ preventScroll: true });
    if (pendingWords) sayLater();
    return true;
  }
  // It opens by itself only where cards are made (the product section), never over a page being read like
  // /pages/docs (D17); asked for (the offer tab), it opens anywhere it is offered. Automatic openings also wait out the
  // first seconds a page is seen, and stay away from a call or an open chat with the assistant. A return opening
  // (back from checkout, the tab again, a later page view) waits for a pause; leaving (exit intent, Back) does not.
  const makesCards = () => !!document.querySelector('[data-pocket-product]');
  // A design in the cart is an order under way (persona round 2, Oct 26, 08-omar F2: 3 s after "Add another design" the
  // offer covered the page): the bar's offer strip is there and nothing opens by itself. The header's count and the
  // drawer's lines say so at once; a return reads the cart itself (backedOut).
  const designInCart = () => Number(document.querySelector('[data-cart-count]')?.textContent) > 0 ||
    !!document.querySelector('#PocketCart [data-cart-items] .cart-line');
  let designing = false; // "Add another design" in this page view
  function autoShow(reason, leaving) {
    if (!makesCards() || interrupting() || settling() || onCall || chatOpen || designing || designInCart()) return false;
    if (!leaving && Date.now() - activeAt < calmMs) return false;
    return show(reason);
  }
  function check() {
    frame = 0;
    if (dialog.open && !dialog.hasAttribute('data-offer-early')) {
      if (challengeShowing()) letChallengeThrough();
      return;
    }
    if (excludedPage) return;
    if (responsePending) {
      if (show('signup_response', true)) responsePending = false;
      return;
    }
    // Exposure only: reaching the pack no longer opens the offer, because that interrupted configuring.
    if (reached) return;
    const pack = document.querySelector('[data-pocket-product] form.product-form .quantity-section');
    if (visible(pack) && !pack.closest('dialog')) {
      const rect = pack.getBoundingClientRect();
      const viewportTop = root.visualViewport?.offsetTop || 0;
      if (rect.width > 0 && rect.height > 0 && rect.top <= viewportTop + 1 && root.scrollY > 0) {
        reached = true;
        capture('pf_email_offer_trigger_reached');
      }
    }
  }
  function schedule() {
    if (!frame) frame = root.requestAnimationFrame(check);
  }
  // Oct 21: the 20 s preview-idle opening is gone. A shown preview is still noted, but never opens the offer.
  function previewShown() {
    if (!makesCards()) return;
    previewAt = Date.now();
  }
  function close(reason) {
    if (!dialog.open) return;
    responsePending = false; // closed before this script took over an early opening: it stays closed
    dialog.removeAttribute('data-offer-early');
    dialog.close();
    release();
    capture('pf_email_offer_dismissed', { method: reason });
    if (previousFocus?.isConnected && visible(previousFocus) &&
      !previousFocus.matches('input,textarea,select')) previousFocus.focus({ preventScroll: true });
  }
  // Shopify's captcha holds the signup while it gets its token, and when it asks for a visible challenge it draws it at
  // the end of the page, outside this dialog. A modal dialog covers the rest of the page and makes it inert, so that
  // challenge could not be clicked. Once a submit is held, or a challenge shows, the dialog stays open in the same place
  // over the same dimmed page but not modal, until the post goes ahead (the page leaves) or the shopper closes it.
  let held = false, scrim = null;
  const challengeFrame = /captcha.*(?:[#&]frame=challenge\b|\/bframe\b)/i; // hCaptcha's challenge, reCAPTCHA's
  function challengeShowing() {
    return !held && Array.from(document.querySelectorAll('iframe')).some(node => {
      if (dialog.contains(node) || !challengeFrame.test(node.src || '') || !visible(node)) return false;
      const r = node.getBoundingClientRect(); // a challenge waiting off-screen is not showing
      return r.bottom > 0 && r.right > 0 && r.top < root.innerHeight && r.left < root.innerWidth;
    });
  }
  function letChallengeThrough() {
    if (!dialog.open || held) return;
    const focused = dialog.contains(document.activeElement) ? document.activeElement : null;
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.className = 'pf-email-offer__scrim';
      scrim.setAttribute('aria-hidden', 'true');
      scrim.addEventListener('click', () => close('backdrop'));
      dialog.before(scrim);
    }
    held = true;
    scrim.hidden = false;
    dialog.setAttribute('data-offer-held', '');
    dialog.close();
    dialog.show();
    focused?.focus({ preventScroll: true });
  }
  function release() {
    if (!held) return;
    held = false;
    dialog.removeAttribute('data-offer-held');
    scrim.hidden = true;
  }
  function newNonce() {
    const bytes = new Uint8Array(16);
    if (!root.crypto?.getRandomValues) return '';
    root.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  function setStatus(box, text) {
    const status = box?.querySelector('[data-launch-status]');
    if (status) status.textContent = text;
  }
  /* What a screen reader hears from the success state goes through its one polite live region (data-launch-live):
     "Your code is ready: <code>" once, "Copied" after each copy, and a message when no code can be shown. The visible
     status line is not live, so nothing is said twice. A live region is only heard once it is on screen, so words
     that come before the offer opens are said just after it opens. */
  function say(box, text) {
    const live = box?.querySelector('[data-launch-live]');
    if (!live || !text) return;
    if (!dialog.open) { pendingWords = text; return; }
    // The same words again (a second copy) still change the region, so they are said again.
    live.textContent = live.textContent === text ? text + '\u00a0' : text;
  }
  function sayLater() {
    root.clearTimeout(wordsTimer);
    wordsTimer = root.setTimeout(() => {
      const text = pendingWords;
      pendingWords = '';
      say(form.querySelector('[data-pf-email-result="success"]'), text);
    }, 250);
  }
  // Without the Clipboard API (an older browser, a page that is not secure): a hidden field is selected and copied.
  function copyWithSelection(box, code) {
    const area = document.createElement('textarea');
    area.value = code;
    area.setAttribute('readonly', '');
    area.setAttribute('aria-hidden', 'true');
    area.tabIndex = -1;
    area.className = 'pf-email-offer__sr ph-no-capture';
    area.setAttribute('data-ph-no-capture', '');
    const back = document.activeElement;
    box.appendChild(area);
    let copied = false;
    try {
      area.focus({ preventScroll: true });
      area.select();
      area.setSelectionRange(0, code.length);
      copied = document.execCommand('copy') === true;
    } catch (_) { /* Not copied: the code is selected on the page instead. */ }
    area.remove();
    if (back && back !== document.body) back.focus?.({ preventScroll: true });
    return copied;
  }
  async function copyCode(box, button) {
    const slot = box.querySelector('[data-launch-code]');
    const code = slot?.textContent?.trim() || '';
    if (!code) return;
    let copied = false;
    try {
      await root.navigator.clipboard.writeText(code);
      copied = true;
    } catch (_) {
      copied = copyWithSelection(box, code);
    }
    if (!copied) {
      // Nothing was copied, so nothing says it was: the code is selected for the shopper's own copy.
      try { root.getSelection()?.selectAllChildren(slot); } catch (_) {}
      return;
    }
    button.dataset.copyLabel = button.dataset.copyLabel || button.textContent.trim();
    const done = box.dataset.copiedLabel || 'Copied';
    button.textContent = done;
    button.setAttribute('data-copied', '');
    say(box, done);
    root.clearTimeout(copyTimer);
    copyTimer = root.setTimeout(() => {
      button.textContent = button.dataset.copyLabel;
      button.removeAttribute('data-copied');
    }, 2000);
  }
  function showCode(box, code) {
    const slot = box.querySelector('[data-launch-code]');
    const button = box.querySelector('[data-launch-copy]');
    if (!slot || !button) return;
    slot.textContent = code;
    slot.hidden = false;
    const wait = box.querySelector('[data-launch-wait]');
    if (wait) wait.hidden = true;
    button.hidden = false;
    box.dataset.launchReveal = 'code';
    setStatus(box, 'We’ll add it at checkout on this device, so there’s nothing to paste. It can’t be combined with other discount codes.');
    if (!button.dataset.copyReady) {
      button.dataset.copyReady = 'true';
      button.addEventListener('click', () => { copyCode(box, button); });
    }
    if (readyFor !== code) {
      readyFor = code;
      say(box, (box.dataset.readyLabel || 'Your code is ready: [code]').replace('[code]', code));
    }
  }
  // No code will show (the offer is closed, or this email cannot get one): the empty ticket goes, the reason stays.
  function noCode(box, message) {
    const ticket = box.querySelector('[data-launch-ticket]');
    if (ticket) ticket.hidden = true;
    setStatus(box, message);
    say(box, message);
  }
  function showRetry(box, message) {
    setStatus(box, message);
    say(box, message);
    const retry = box.querySelector('[data-launch-retry]');
    if (!retry) return;
    retry.hidden = false;
    if (!retry.dataset.bound) {
      retry.dataset.bound = 'true';
      retry.addEventListener('click', () => {
        box.dataset.launchRequested = '';
        retry.hidden = true;
        setStatus(box, 'Getting your code…');
        revealSignupCode();
      });
    }
  }
  async function revealSignupCode() {
    const box = form.querySelector('[data-pf-email-result="success"]');
    if (!box || box.dataset.launchRequested === 'true') return;
    // The code is minted for the arm whose words the shopper read: wait for it (the head's 2 s at most), as show() does.
    if (armPending()) {
      document.addEventListener('pf:offer-arm', () => revealSignupCode(), { once: true });
      return;
    }
    box.dataset.launchRequested = 'true';
    const email = (box.dataset.signupEmail || storage('sessionStorage', emailKey) || storage('localStorage', emailKey) || '').trim().toLowerCase();
    const nonce = storage('sessionStorage', nonceKey) || storage('localStorage', nonceKey) || '';
    // The host comes from pocket-core.js (production, or a local/staging Worker on preview hosts only).
    const endpoint = dialog.dataset.issuePath ? (root.PocketCore?.workerBase?.() || '') + dialog.dataset.issuePath : (dialog.dataset.issueUrl || '');
    if (!email || !endpoint) {
      showRetry(box, 'We couldn’t show your code yet. Tap Show my code again.');
      return;
    }
    setStatus(box, 'Getting your code…');
    const maxAttempts = 20;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const body = { email, marketingConsent: true, arm: pageArm() };
        if (/^[a-f0-9]{32}$/.test(nonce)) body.nonce = nonce;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await response.json();
        if (json.status === 'pending' || json.status === 'temporary_error') {
          await new Promise((resolve) => root.setTimeout(resolve, Math.min(1500, 350 * (attempt + 1))));
          continue;
        }
        if ((json.status === 'issued' || json.status === 'already_issued') && typeof json.code === 'string' && json.code) {
          const retry = box.querySelector('[data-launch-retry]');
          if (retry) retry.hidden = true;
          showCode(box, json.code);
          capture('pf_email_signup_code_shown', { offer_arm: json.arm || 'legacy' });
          rememberCode(json.code, json.arm);
          syncCart();
          return;
        }
        if (json.status === 'campaign_closed') {
          noCode(box, 'This offer isn’t available right now. No code was issued.');
          return;
        }
        if (json.status === 'ineligible') {
          // A prior buyer (by email or by the network a paid order came from): no code, and the offer never shows again.
          forgetCode();
          markPurchased('signup_ineligible');
        }
        noCode(box, 'The first-order code is for first orders only, so no code was issued. Questions? Email bryton@getpocketfactory.com.');
        return;
      } catch (_) {
        await new Promise((resolve) => root.setTimeout(resolve, 500));
      }
    }
    showRetry(box, 'We couldn’t show your code yet. Your email is saved. Tap Show my code again.');
  }
  dialog.querySelectorAll('[data-offer-close]').forEach(button => button.addEventListener('click', () => close('button')));
  dialog.addEventListener('cancel', event => { event.preventDefault(); close('escape'); });
  // Not modal while held: Escape is not a close request then, so it is handled here. Any other close drops the dim.
  // While modal, Tab and Shift+Tab go round the offer's own controls (the page behind is inert); while held, focus is
  // free to reach the captcha's challenge outside it.
  const tabbable = () => Array.from(dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]'))
    .filter(node => node.tabIndex >= 0 && !node.disabled && node.type !== 'hidden' && visible(node));
  dialog.addEventListener('keydown', event => {
    if (held && event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); close('escape'); }
    if (held || event.key !== 'Tab' || event.defaultPrevented || !dialog.open) return;
    const items = tabbable();
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1], at = document.activeElement;
    if (event.shiftKey && (at === first || at === dialog)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && at === last) { event.preventDefault(); first.focus(); }
  });
  dialog.addEventListener('close', () => { if (!dialog.open) release(); });
  // A tap on the dimmed page closes the offer. A press that starts inside the offer (selecting the typed email, say)
  // and is let go outside it is not a tap on the page.
  const outside = (event, r) => event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
  let pressedInside = false;
  dialog.addEventListener('pointerdown', event => {
    pressedInside = event.target !== dialog || !outside(event, dialog.getBoundingClientRect());
  });
  dialog.addEventListener('click', event => {
    const inside = pressedInside;
    pressedInside = false;
    if (event.target !== dialog || inside) return;
    if (outside(event, dialog.getBoundingClientRect())) close('backdrop');
  });
  root.addEventListener('scroll', schedule, { passive: true });
  root.addEventListener('resize', schedule, { passive: true });
  root.visualViewport?.addEventListener('scroll', schedule, { passive: true });
  root.visualViewport?.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('focusout', schedule, true);
  document.addEventListener('close', schedule, true);
  document.addEventListener('visibilitychange', schedule);
  document.addEventListener('shopify:section:load', schedule);
  new MutationObserver(schedule).observe(document.body, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['open', 'hidden', 'inert', 'aria-hidden', 'aria-modal', 'class', 'style']
  });
  if (typeof ResizeObserver === 'function') new ResizeObserver(schedule).observe(document.body);
  // Purpose, pack and design changes (configurator form, homepage website form, card editor), and typing or choosing
  // anything else on the page outside the offer (the chat, a link field), start the quiet period.
  const active = () => { activeAt = Date.now(); };
  const changed = event => {
    active();
    const target = event?.target;
    if (!event || target?.closest?.(composer) || (target?.nodeType === 1 && !dialog.contains(target))) changedAt = activeAt;
  };
  document.addEventListener('input', changed, true);
  document.addEventListener('change', changed, true);
  ['pocket:card-config', 'pocket:design-change', 'pocket:design-words', 'pocket:hero-edit', 'pocket:upload-artwork',
    'pocket:composer-submit', 'pocket:remote-applied'].forEach(type => document.addEventListener(type, () => changed()));
  // A call with the assistant, or its chat open (pocket-call.js, pocket-textcall.js), is the shopper's conversation.
  document.addEventListener('pocket:call-state', event => { onCall = !!event.detail?.onCall; });
  document.addEventListener('pocket:textcall', event => {
    if (event.detail && 'open' in event.detail) chatOpen = !!event.detail.open;
  });
  // The page seen again (back/forward cache, this tab coming back) starts the first seconds again.
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') seenAt = Date.now(); });
  root.addEventListener('pageshow', event => { if (event.persisted) seenAt = Date.now(); });
  ['pointerdown', 'keydown', 'wheel', 'touchstart', 'visibilitychange'].forEach(type => document.addEventListener(type, active, { capture: true, passive: true }));
  root.addEventListener('scroll', active, { passive: true });
  // A shown preview (brand applied, share link hydrated, or a saved brand on load) is noted; since Oct 21 it no longer
  // opens the offer (the 20 s preview-idle opening is gone). Applying a brand is a change: it starts the quiet period.
  document.addEventListener('pocket:brand-path', () => { changed(); previewShown(); });
  document.addEventListener('pocket:preview-hydrated', previewShown);
  const personalized = () => document.documentElement.hasAttribute('data-pf-personalized');
  new MutationObserver(() => { if (personalized()) previewShown(); })
    .observe(document.documentElement, { attributes: true, attributeFilter: ['data-pf-personalized'] });
  if (personalized()) previewShown();
  // A design going into the cart keeps the offer away, as a checkout does; only a real checkout start
  // (pocket-analytics.js) is counted as pf_email_offer_checkout_started, never a cart update.
  let checkoutCounted = false;
  const stopForCheckout = started => {
    if (started && !checkoutCounted && claimed) { checkoutCounted = true; capture('pf_email_offer_checkout_started'); }
    checkout = true;
    if (dialog.open) close('checkout');
  };
  document.addEventListener('pocket:checkout-started', () => stopForCheckout(true));
  document.addEventListener('pocket:cart-updated', () => stopForCheckout(false));
  document.addEventListener('pocket:cart-error', () => { checkout = false; checkoutCounted = false; paymentUntil = 0; schedule(); });
  root.addEventListener('pageshow', event => {
    if (event.persisted) { checkout = false; checkoutCounted = false; paymentUntil = 0; }
    schedule();
  });
  document.addEventListener('click', event => {
    const target = event.target;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [target];
    const wallet = path.some(node => node?.matches?.('.shopify-payment-button,shopify-accelerated-checkout,shopify-accelerated-checkout-cart'));
    const checkoutLink = target?.closest?.('a[href*="/checkout"],button[name="checkout"]');
    if (wallet || checkoutLink) {
      paymentUntil = Date.now() + 10000;
      root.setTimeout(schedule, 10050);
    }
  }, true);
  // What the signup post needs from this script: the shopper's email kept for the code, and a one-time nonce in the
  // customer tags. It is written when the dialog opens and as the email is typed, and again on submit, so the post
  // carries them even when another submit listener (Shopify's captcha) holds or stops the submit event first.
  function prepareSignup(fresh) {
    const emailInput = form.querySelector('input[name="contact[email]"]');
    const emailValue = String(emailInput?.value || '').trim().toLowerCase();
    if (emailValue) storage('sessionStorage', emailKey, emailValue);
    const tags = form.querySelector('input[name="contact[tags]"]');
    const posted = String(tags?.value || '').split(',').map((tag) => tag.trim()).find((tag) => tag.startsWith('pf-email50-n'));
    const nonce = (!fresh && posted && posted.slice('pf-email50-n'.length)) || newNonce();
    if (nonce) {
      storage('sessionStorage', nonceKey, nonce);
      storage('localStorage', nonceKey, nonce);
      if (tags) {
        // The nonce, and the A/B arm whose words the shopper read (pf-offer-<arm>, one only), go in with the signup.
        const kept = String(tags.value || '').split(',').map((tag) => tag.trim()).filter((tag) => tag && !tag.startsWith('pf-email50-n') && !tag.startsWith('pf-offer-'));
        kept.push('pf-email50-n' + nonce, 'pf-offer-' + pageArm());
        tags.value = kept.join(',');
      }
    }
    return emailValue;
  }
  form.querySelector('input[name="contact[email]"]')?.addEventListener('input', () => prepareSignup(false));
  // Consent stays required (owner decision), but the box is not "invalid" before anyone tries (persona round 2, Oct 26,
  // 10-daniel D-09: a screen reader said "invalid entry" on opening; 04-jess R2-5, 08-omar F9: only the browser's bubble
  // said why). The page keeps `required` until this script runs; from then a send without it says what to do in the
  // offer, tied to the box (aria-describedby), and focus goes to the box.
  const consent = form.querySelector('input[name="contact[accepts_marketing]"]'), consentNote = form.querySelector('[data-offer-consent-error]');
  if (consent?.required) { consent.required = false; consent.setAttribute('aria-required', 'true'); }
  function askConsent() {
    if (consentNote) { consentNote.hidden = false; consent.setAttribute('aria-describedby', consentNote.id); }
    consent.setAttribute('aria-invalid', 'true');
    consent.focus();
  }
  consent?.addEventListener('change', () => {
    if (!consent.checked) return;
    if (consentNote) consentNote.hidden = true;
    consent.removeAttribute('aria-invalid');
    consent.removeAttribute('aria-describedby');
  });
  // The email the same way (AUDIT-1 A-02): the form checks itself (noValidate), so the browser's own bubble ("Please
  // fill out this field.") never shows in the offer. A send without a usable email says why under the field, tied to it
  // (aria-describedby), with focus on it; typing takes the line away. Without this script the browser checks as before.
  const emailField = form.querySelector('input[name="contact[email]"]'), emailNote = form.querySelector('[data-offer-email-error]');
  const emailDescribed = emailField?.getAttribute('aria-describedby') || '';
  if (emailField && emailNote) form.noValidate = true;
  function askEmail() {
    emailNote.textContent = emailField.validity.valueMissing ? emailNote.dataset.needed : emailNote.dataset.invalid;
    emailNote.hidden = false;
    emailField.setAttribute('aria-invalid', 'true');
    emailField.setAttribute('aria-describedby', emailNote.id);
    emailField.focus();
  }
  emailField?.addEventListener('input', () => {
    if (!emailNote || emailNote.hidden) return;
    emailNote.hidden = true;
    emailField.removeAttribute('aria-invalid');
    if (emailDescribed) emailField.setAttribute('aria-describedby', emailDescribed); else emailField.removeAttribute('aria-describedby');
  });
  // One real submit posts once and counts once: a double tap, click plus touchend, or a repeated submit event while
  // the first is on its way is dropped. A submit another script cancelled (bot check) does not block the next. The
  // listener sits on the window in the capture phase, so it runs before any page-level submit hook. A submit Shopify's
  // captcha holds is its own: nothing here cancels it or posts the form instead (GATE, Oct 19: a fetch sent before the
  // captcha's token landed on its 400 page).
  // C-01 (persona round 2, Oct 26): the browser's post reloaded the page, and on a slow phone the shopper waited 22 s at
  // the top of a blank card for the code. A submit nobody holds (no captcha hook, or the captcha's own submit with its
  // token) is sent to the same address by fetch instead (sendHere, the submit's last listener) and the code shows in
  // this offer. Anything but Shopify's "posted" answer (its /challenge page, a 400 for a missing token, an error, no
  // answer in 20 s) sends the form again the usual way, through requestSubmit: the captcha hook sees it, the browser posts.
  const submitButton = form.querySelector('[type="submit"]'), sendingLive = form.querySelector('[data-offer-sending]');
  let posting = null, postedAt = 0, counted = '', inPlace = null, resending = false, nativeOnly = false, sendLabel = null, busyTimer = 0;
  const sentHere = new WeakSet();
  // "Sending your code…" from the moment the signup goes until the code shows or the page goes, said once through the
  // form's own live region; Send is back after 15 s if nothing came of it.
  function sending(on) {
    root.clearTimeout(busyTimer);
    if (!submitButton?.isConnected) return;
    const words = submitButton.dataset.sendingLabel || 'Sending your code…';
    if (!on) {
      submitButton.removeAttribute('aria-busy');
      if (sendLabel) submitButton.replaceChildren(...sendLabel.map(node => node.cloneNode(true)));
      if (sendingLive) sendingLive.textContent = '';
      return;
    }
    sendLabel = sendLabel || Array.from(submitButton.childNodes, node => node.cloneNode(true));
    submitButton.setAttribute('aria-busy', 'true');
    if (submitButton.textContent !== words) submitButton.textContent = words;
    if (sendingLive && sendingLive.textContent !== words) sendingLive.textContent = words;
    busyTimer = root.setTimeout(() => { if (!inPlace) sending(false); }, 15000);
  }
  // A held submit the captcha posts itself later (HTMLFormElement.prototype.submit fires no submit event) still builds
  // the form's data: the page goes from here.
  form.addEventListener('formdata', () => { if (posting && !claimed) sending(true); });
  root.addEventListener('submit', event => {
    if (event.target !== form) return;
    rememberCard();
    if (inPlace && !resending) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (!resending) {
      if (posting && !posting.defaultPrevented && Date.now() - postedAt < 10000) {
        event.preventDefault();
        return;
      }
      if (!form.checkValidity()) {
        // The form checks itself (above): nothing is sent, and the email says why.
        if (form.noValidate) { event.preventDefault(); event.stopImmediatePropagation(); if (!emailField.validity.valid) askEmail(); }
        return;
      }
      // Not sent, so no other submit listener (Shopify's captcha) acts on it either.
      if (consent && !consent.checked) { event.preventDefault(); event.stopImmediatePropagation(); askConsent(); return; }
      const emailValue = prepareSignup(true);
      if (emailValue) storage('localStorage', emailKey, emailValue);
      storage('sessionStorage', pendingKey, '1');
      storage('sessionStorage', placeKey, JSON.stringify({ path: root.location.pathname, y: Math.round(root.scrollY || 0) }));
      if (counted !== (emailValue || '-')) {
        counted = emailValue || '-';
        capture('pf_email_signup_submitted');
      }
    }
    posting = event;
    postedAt = Date.now();
    // Added again on every submit, so it is the window's last submit listener: it runs after every other one.
    root.removeEventListener('submit', sendHere);
    root.addEventListener('submit', sendHere);
    root.setTimeout(() => {
      if (sentHere.has(event)) return;
      // Held by another submit listener (Shopify's captcha while it gets its token): its challenge must be clickable.
      if (event.defaultPrevented) { letChallengeThrough(); return; }
      sending(true);
    }, 0);
  }, true);
  function sendHere(event) {
    if (event !== posting || event.defaultPrevented || nativeOnly || typeof root.fetch !== 'function' || typeof root.FormData !== 'function') return;
    event.preventDefault();
    sentHere.add(event);
    inPlace = sendInPlace(event.submitter).finally(() => { inPlace = null; });
  }
  async function sendInPlace(submitter) {
    sending(true);
    const controller = typeof root.AbortController === 'function' ? new root.AbortController() : null;
    const late = root.setTimeout(() => controller?.abort(), 20000);
    let back = '';
    try {
      const response = await root.fetch(form.action, {
        method: 'POST', body: new root.URLSearchParams(new root.FormData(form)), credentials: 'same-origin',
        headers: { Accept: 'text/html' }, signal: controller?.signal
      });
      const url = new URL(response.url || '', root.location.href);
      if (response.ok && url.searchParams.get('customer_posted') === 'true') back = url.pathname + url.search;
      // A page that shows the success state without the marker is still a signup taken: never post it twice.
      else if (response.ok && !/\/challenge\/?$/.test(url.pathname) && /data-pf-email-result="success"/.test(await response.text())) back = url.pathname + url.search;
    } catch (_) { /* No answer: the browser posts it instead. */ }
    root.clearTimeout(late);
    try { controller?.abort(); } catch (_) { /* The page Shopify answered with is never read. */ }
    if (back) { signedUpHere(back); return; }
    nativeOnly = true;
    resending = true;
    try {
      const own = submitter?.form === form ? submitter : null;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(own);
      else (own || submitButton)?.click();
    } catch (_) { /* Send is back after 15 s, to press again. */ }
    resending = false;
  }
  // Shopify took the signup: its success state (the snippet's template, as on the page it returns to) takes the
  // form's place, the offer is modal again if the captcha had held it, and focus goes to the offer.
  function signedUpHere(back) {
    const box = form.querySelector('template[data-pf-email-success]')?.content?.firstElementChild?.cloneNode(true);
    if (!box) { root.location.assign(back + '#PFEmailOfferForm'); return; }
    box.dataset.signupEmail = storage('sessionStorage', emailKey) || storage('localStorage', emailKey) || '';
    if (held && dialog.open) {
      release();
      dialog.close();
      dialog.setAttribute('data-offer-instant', '');
      try { dialog.showModal(); } catch (_) { dialog.show(); }
    }
    Array.from(form.children).forEach(node => { if (!node.matches('input[type="hidden"]')) node.remove(); });
    form.append(box);
    dialog.setAttribute('aria-describedby', 'PFEmailOfferStatus');
    if (dialog.open) dialog.focus({ preventScroll: true });
    storage('sessionStorage', placeKey, null);
    storage('sessionStorage', 'pf.email50.returnCard', null);
    signedUp(true);
  }
  // Signed up (here, or on the page Shopify returns to): the code is asked for once.
  function signedUp(here) {
    claimed = true;
    storage('localStorage', claimedKey, '1');
    storage('sessionStorage', pendingKey, '');
    capture('pf_email_signup_form_success', here ? { in_place: true } : undefined);
    captureOffer('popup_signup', { stage: 'form_success' });
    revealSignupCode();
  }
  root.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    posting = null;
    counted = '';
    inPlace = null;
    nativeOnly = false;
    sending(false);
  });
  // Back where they were (C-01): the page Shopify returns to scrolls to where the signup was sent from, and again once
  // it is laid out, unless the shopper has moved it.
  function returnPlace() {
    let at = null;
    try { at = JSON.parse(storage('sessionStorage', placeKey) || 'null'); } catch (_) {}
    if (!at || at.path !== root.location.pathname || !(at.y > 0)) return;
    const go = () => { try { if (dialog.open || root.scrollY < 2) root.scrollTo(0, at.y); } catch (_) {} };
    const done = () => { go(); storage('sessionStorage', placeKey, null); };
    go();
    if (document.readyState === 'complete') done(); else root.addEventListener('load', done, { once: true });
  }
  // The page Shopify returns to: the dialog is described by what happened (the code's status line, or the form error).
  if (result === 'success' && form.querySelector('#PFEmailOfferStatus')) dialog.setAttribute('aria-describedby', 'PFEmailOfferStatus');
  else if (result === 'error' && form.querySelector('#PFEmailOfferError')) dialog.setAttribute('aria-describedby', 'PFEmailOfferError PFEmailOfferDescription');
  if (result && (storage('sessionStorage', pendingKey) === '1' || root.location.hash === '#PFEmailOfferForm')) {
    // Shown from the page's first paint (the snippet's inline opener) and closed before this script ran: not again.
    responsePending = !dialog.hasAttribute('data-offer-closed-early');
    if (!responsePending) { shown = true; storage('sessionStorage', shownKey, '1'); }
    returnPlace();
    if (result === 'success') {
      signedUp(false);
      // Back from checkout or a reload must not open the signup again: drop Shopify's posted marker from this entry.
      try {
        const url = new URL(root.location.href);
        url.searchParams.delete('customer_posted');
        root.history.replaceState(root.history.state, '', url.pathname + url.search);
      } catch (_) {}
    }
  }
  const qaButton = document.getElementById('PFEmailOfferQA');
  if (qa && qaButton) {
    qaButton.hidden = false;
    qaButton.addEventListener('click', () => show('preview_test', true));
  }
  /* Oct 21: buying intent, then backing out. Only a tap on a real buy control (not "Preview my cards") or Shopify's
     confirmed cart add arms the offer; it opens when the shopper comes back without ordering or tries to leave. */
  const fineHover = () => !!root.matchMedia?.('(hover: hover) and (pointer: fine) and (min-width: 900px)').matches;
  let trapArmed = false, trapTimer = 0;
  function armBackTrap() {
    // Phones: after a buy tap that did not leave the page (cart drawer, wallet sheet closed), the next Back opens the
    // offer once instead of leaving. One extra history entry at the same address, only while the offer can open.
    if (trapArmed || fineHover() || !eligible() || excludedPage || designing || designInCart()) return;
    try { root.history.pushState(Object.assign({}, root.history.state, { pfOfferTrap: 1 }), '', root.location.href); trapArmed = true; } catch (_) {}
  }
  root.addEventListener('popstate', event => {
    if (!trapArmed || event.state?.pfOfferTrap) return;
    trapArmed = false;
    // Could not open right now (a wallet sheet in its 10 s, a just-made change, the page's first seconds): finish the
    // Back they asked for.
    if (!(intent() && !purchased() && autoShow('back_attempt', true))) { try { root.history.back(); } catch (_) {} }
  });
  function noteIntent(source) {
    if (excludedPage || purchased()) return;
    recordIntent(source);
    root.clearTimeout(trapTimer);
    trapTimer = root.setTimeout(() => { if (document.visibilityState === 'visible') armBackTrap(); }, 2500);
  }
  const buyControl = '.shopify-payment-button,shopify-accelerated-checkout,shopify-accelerated-checkout-cart,' +
    '[data-pay-area],button[name="add"],button[name="checkout"],a[href*="/checkout"],[data-checkout],[data-purchase],[data-pfx-buy],[data-add],' +
    '[data-quick-buy],[data-pf-quick-buy]';
  document.addEventListener('click', event => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const node = path.find(el => el?.matches?.(buyControl));
    if (!node || dialog.contains(node)) return;
    // A Buy that reads "Preview…" would be designing, not buying (none does since F1: Buy is Buy before a preview too).
    if (node.matches('[data-purchase],[data-pfx-buy]') && /preview/i.test(node.textContent || '')) return;
    const kind = node.matches('.shopify-payment-button,shopify-accelerated-checkout,shopify-accelerated-checkout-cart,[data-pay-area]') ? 'wallet'
      : node.matches('button[name="checkout"],a[href*="/checkout"],[data-checkout]') ? 'checkout' : node.matches('button[name="add"],[data-add]') ? 'add' : 'buy';
    noteIntent(kind);
  }, true);
  document.addEventListener('pocket:cart-updated', () => noteIntent('cart_add'));
  document.addEventListener('pocket:checkout-started', () => noteIntent('checkout_started'));
  // Back without ordering? A cart that still has items is an order under way: no opening (persona round 2, Oct 26). A
  // checked-out cart (Shopify gives the browser a new cart) is an order. An empty cart after a wallet tap is unclear: only
  // a return to this same page (bfcache, tab) counts then, never a fresh page view, so a buyer coming back from the
  // thank-you page is not asked.
  async function backedOut(sameView) {
    const seen = intent();
    if (!seen || purchased()) return false;
    let count = null;
    try { count = (await cartJson('cart.js'))?.item_count; } catch (_) {}
    const now = cookie('cart');
    if (count > 0 || (count == null && designInCart())) return false;
    if (seen.cart && now !== seen.cart && count === 0) { markPurchased('cart_checked_out'); return false; }
    return sameView;
  }
  let returnTry = 0, returning = false, returnTimer = 0;
  // One return waits at a time, and the first one names it (a page restored from the back/forward cache reports a
  // visible tab too).
  function returnLater(reason, sameView, ms) {
    if (returnTimer) return;
    returnTimer = root.setTimeout(() => { returnTimer = 0; returned(reason, sameView); }, ms);
  }
  async function returned(reason, sameView) {
    if (returning || excludedPage || dialog.open || !eligible() || !intent()) return;
    if (Date.now() - intent().t < 1500) return; // the tap itself, not a return
    // The first seconds after the page or the tab is seen are the shopper's: the cart is checked once they are over.
    if (settling()) { returnLater(reason, sameView, (seenAt ? seenAt + settleMs - Date.now() : settleMs) + 50); return; }
    returning = true;
    let back = false;
    try { back = await backedOut(sameView); } finally { returning = false; }
    if (!back) return;
    if (autoShow(reason)) return;
    // Blocked for now (keyboard, another dialog, a just-made change, scrolling): try again shortly, for up to ~30 s.
    if (++returnTry < 30) returnLater(reason, sameView, 1000);
  }
  root.addEventListener('pageshow', event => { if (event.persisted) { returnTry = 0; returned('back_from_checkout', true); } });
  // "Add another design" (pocket.js): the shopper is making a second design for this order. Nothing opens by itself for
  // the rest of this page view, and a return opening still waiting is dropped.
  ['pocket:new-design', 'pocket:design-reset'].forEach(type => document.addEventListener(type, () => {
    designing = true;
    changed();
    root.clearTimeout(returnTimer);
    returnTimer = 0;
    returnTry = 30;
  }));
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    if (!hiddenAt || !intent() || intent().t > hiddenAt) return;
    // A page restored from the back/forward cache reports visible too; let its pageshow name the return first.
    root.setTimeout(() => { if (dialog.open) return; checkout = false; checkoutCounted = false; paymentUntil = 0; returnTry = 0; returned('tab_return', true); }, 150);
  });
  if (intent() && !purchased() && !result) {
    const go = () => { returnTry = 0; returned('return_pageview', false); };
    if (document.readyState === 'complete') root.setTimeout(go, 800); else root.addEventListener('load', () => root.setTimeout(go, 800), { once: true });
  }
  document.addEventListener('mouseout', event => {
    if (event.relatedTarget || event.clientY > 0 || !fineHover()) return;
    const seen = intent();
    if (!seen || purchased() || Date.now() - seen.t < 1500) return;
    autoShow('exit_intent_after_buy', true);
  });
  // The bar's code line follows the card's pack, finish and color (its price on the bar changes with them).
  const barCode = () => { try { paintBarCode(); } catch (_) { /* The bar keeps its price. */ } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', barCode, { once: true }); else barCode();
  document.querySelectorAll('[data-pfx-summary-total]').forEach(node => {
    if (root.MutationObserver) new root.MutationObserver(barCode).observe(node, { childList: true, characterData: true, subtree: true });
  });
  // And whether another design is in the cart (pocket.js sets data-others on the bar).
  document.querySelectorAll('[data-buy-bar]').forEach(node => {
    if (root.MutationObserver) new root.MutationObserver(barCode).observe(node, { attributes: true, attributeFilter: ['data-others'] });
  });
  root.PocketEmailOffer = {
    available: () => !offerHeld() && !excludedPage && (!Number.isFinite(expiresAt) || Date.now() < expiresAt) && !claimed &&
      dialog.dataset.subscribed !== 'true' && dialog.dataset.hasOrdered !== 'true' && !purchased(),
    open: reason => show(reason || 'manual', true)
  };
  schedule();
})(typeof window !== 'undefined' ? window : globalThis);
